import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const cases = [
  {
    tag: 'Entertainment',
    client: 'Cochise Country Music Festival',
    metric: '6.5X',
    metricLabel: 'Return on Ad Spend',
    secondaryMetric: '3x Ticket Sales',
    secondaryLabel: 'in 90 Days',
    description:
      'We took the Cochise Country Music Festival viral. In 90 days: 1.6 million views, hundreds of thousands of people engaging with content, and ticket sales literally doubled. The algorithm found their audience — we made them obsessed.',
    duration: '90-day campaign',
    image: 'https://static.readdy.ai/image/34db628ba5af2970830d5ecb268acaef/f81759d7027ebd2c17baa5832e005fe5.webp',
  },
  {
    tag: 'Automotive',
    client: 'House of Cars AZ',
    metric: '+187%',
    metricLabel: 'Return on Ad Spend',
    secondaryMetric: '340 Qualified Leads',
    secondaryLabel: 'in 30 Days',
    description:
      'Deployed hyper-targeted Meta campaigns for House of Cars AZ, one of Arizona\'s most recognized dealerships. Our AI audience algorithm identified in-market buyers and cut wasted ad spend dramatically.',
    duration: '30-day campaign',
    image: 'https://static.readdy.ai/image/34db628ba5af2970830d5ecb268acaef/faf307a3d138bd2b62b5c2a91795bc20.jpeg',
  },
  {
    tag: 'Real Estate',
    client: '1912 Real Estate Group — Tucson, AZ',
    metric: '780K',
    metricLabel: 'Views in 30 Days',
    secondaryMetric: '$3.2M',
    secondaryLabel: 'in New Listings Closed',
    description:
      'Partnered with 1912 Real Estate Group in Tucson, Arizona to dominate the local market. Our video-first Meta strategy and AI audience targeting flooded their pipeline with qualified buyers and sellers.',
    duration: '30-day campaign',
    image: 'https://static.readdy.ai/image/34db628ba5af2970830d5ecb268acaef/61da25ecbc9b5aeeef25e9295b866ea5.jpeg',
  },
  {
    tag: 'Legal',
    client: 'South Florida Business Law Group',
    metric: '4.3X',
    metricLabel: 'Cost-Per-Case Reduction',
    secondaryMetric: '41 New Cases',
    secondaryLabel: 'in 45 Days',
    description:
      'South Florida Business Law Group needed a scalable client acquisition system. We rebuilt their entire ad funnel and used sequential targeting to reach decision-makers — slashing cost-per-case and filling their pipeline.',
    duration: '45-day campaign',
    image: 'https://static.readdy.ai/image/34db628ba5af2970830d5ecb268acaef/18de35d51ef8fac2b3f16b08c3b20951.png',
  },
];

export default function CaseStudies() {
  const ref = useScrollAnimation();

  return (
    <section id="work" className="bg-black py-24 md:py-32 border-t border-white/5">
      <div ref={ref} className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 fade-in-up">
          <div>
            <p className="text-electric-blue text-xs font-semibold tracking-widest uppercase mb-4">
              Featured Results
            </p>
            <h2 className="font-display font-bold text-white leading-tight"
              style={{ fontSize: 'clamp(2rem, 3.5vw, 3rem)' }}
            >
              Receipts. Not<br />Promises.
            </h2>
          </div>
          <p className="text-white/35 text-sm md:text-base max-w-sm leading-relaxed">
            Real clients. Real budgets. Real results. Every number below is verifiable.
          </p>
        </div>

        {/* Case study grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {cases.map((c, i) => (
            <div
              key={c.client}
              className="fade-in-up group relative bg-dark-card border border-dark-border rounded-lg overflow-hidden card-lift cursor-pointer"
              style={{ transitionDelay: `${i * 0.1}s` }}
            >
              {/* Image */}
              <div className="relative h-52 overflow-hidden">
                <img
                  src={c.image}
                  alt={c.client}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />

                {/* Tag */}
                <div className="absolute top-4 left-4 px-2.5 py-1 rounded-full bg-black/60 border border-white/10 backdrop-blur-sm">
                  <span className="text-white/60 text-xs font-medium tracking-wide">{c.tag}</span>
                </div>

                {/* Primary metric */}
                <div className="absolute bottom-4 left-5">
                  <span className="font-display font-bold text-electric-blue text-glow-blue"
                    style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}
                  >
                    {c.metric}
                  </span>
                  <p className="text-white/60 text-xs mt-0.5">{c.metricLabel}</p>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <h3 className="font-display font-semibold text-white text-base leading-snug">
                    {c.client}
                  </h3>
                  <div className="text-right flex-shrink-0">
                    <div className="text-white font-bold text-sm">{c.secondaryMetric}</div>
                    <div className="text-white/30 text-xs">{c.secondaryLabel}</div>
                  </div>
                </div>
                <p className="text-white/40 text-sm leading-relaxed mb-4">{c.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-white/20 text-xs tracking-wide">{c.duration}</span>
                  <span className="text-electric-blue text-xs font-semibold flex items-center gap-1 group-hover:gap-2 transition-all duration-200 cursor-pointer">
                    View Full Case <i className="ri-arrow-right-line" />
                  </span>
                </div>
              </div>

              {/* Top border on hover */}
              <div className="absolute top-0 left-0 right-0 h-px bg-electric-blue/0 group-hover:bg-electric-blue/50 transition-all duration-500" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
